// src/components/ModelSelector.tsx
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import Select from './ui/Select'; // Using the provided definition
import Tabs from './Tabs';
import { ActionButton } from './ActionPanel';
import {
  Info,
  RotateCcw,
  Sparkles,
  Paintbrush,
  Video
} from '@/components/ui/lucide-memo';
import {
  ALL_MODELS_IN_UI as ALL_MODELS_IN_UI_FROM_REGISTRY,
  buildStudioFrontendModelAccessState,
  CHEAP_STUDIO_MODEL_IDS,
  compareStudioModels,
  filterStudioModelIdsForProjectType,
  MODEL_SELECTOR_MAPPINGS,
} from '#lib/model-registry';
import { clientAccessSuspension, isWithheldAsUnrunnable } from '#lib/access-suspension';
import { resolveFamilyVersion, studioModelFamilyNamed, studioModelFamilyOf, visibleFamilyVersions } from '#lib/studioModelFamilies';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';

type ModelCategory = 'Generate' | 'Edit' | 'Video';
type ModelSubCategory = 'Basic' | 'Controlled' | 'Special' | 'Masked' | 'Full Area' | 'Generate' | 'Edit';
const generateModelSubCategories: ModelSubCategory[] = ['Basic', 'Controlled', 'Special'];
const editModelSubCategories: ModelSubCategory[] = ['Masked', 'Full Area'];

interface ModelInfo {
  id: string;
  name: string;
  category: ModelCategory;
  subCategory?: ModelSubCategory;
}

interface CategoryUIData {
  category: ModelCategory;
  icon: React.ReactNode;
  models: ModelInfo[];
}

const ALL_MODELS_IN_UI = [...ALL_MODELS_IN_UI_FROM_REGISTRY];
  
export const modelMappings: { [id: string]: { name: string; category: ModelCategory; subCategory?: ModelSubCategory;} } =
  MODEL_SELECTOR_MAPPINGS as { [id: string]: { name: string; category: ModelCategory; subCategory?: ModelSubCategory;} };

const allowedModelSet = new Set(ALL_MODELS_IN_UI);
const buildModelHierarchy = (): CategoryUIData[] => {
  const categoriesMap = new Map<ModelCategory, CategoryUIData>([
    ['Generate', { category: 'Generate', icon: <Sparkles size={16} />, models: [] }],
    ['Edit',     { category: 'Edit',     icon: <Paintbrush size={16} />, models: [] }],
    ['Video',    { category: 'Video',    icon: <Video size={16} />, models: [] }],
  ]);

  Object.keys(modelMappings).forEach(id => {
    if (allowedModelSet.has(id)) {
      const mapping = modelMappings[id];
      if(!mapping) return;
      const categoryData = categoriesMap.get(mapping.category);
      if (categoryData) {
        categoryData.models.push({
          id: id,
          name: mapping.name,
          category: mapping.category,
          subCategory: mapping.subCategory,
        });
      } else {
        console.warn(`Model '${id}' has category '${mapping.category}' which is not defined in categoriesMap.`);
      }
    }
  });

  return Array.from(categoriesMap.values()).filter(category => category.models.length > 0);
};

const modelHierarchyData = buildModelHierarchy();

const NARROW_SELECT_STYLE = { width: '110px' };

const findModelDataById = (modelId: string): ModelInfo | undefined => {
    const mapping = modelMappings[modelId];
    if (mapping && ALL_MODELS_IN_UI.includes(modelId)) {
        return { id: modelId, name: mapping.name, category: mapping.category, subCategory: mapping.subCategory };
    }
    return undefined;
}

const findModelIdByName = (modelName: string): string | undefined => {
    for (const id in modelMappings) {
        if (ALL_MODELS_IN_UI.includes(id) && modelMappings[id]?.name === modelName) {
            return id;
        }
    }
    return undefined;
}

interface ModelSelectorProps {
  projectId?: string | null;
  currentModelId: string;
  onModelChange: (newModelId: string) => void;
  onRestoreDefaults: () => void;
}

const ModelSelector: React.FC<ModelSelectorProps> = React.memo(({
  projectId,
  currentModelId,
  onModelChange,
  onRestoreDefaults
}) => {
  const { kernel } = useKernel();
  const userContext = useKernelStore(state => state.userContext);
  
  const organisationCredits = useKernelStore(state => state.organisationCredits);
  const isRestricted = useMemo(() => {
    const scope = userContext?.permissions?.accessScope;
    if (scope === 'ALL_MODELS') return false;
    if (scope === 'CHEAP_MODELS_ONLY') return true;
    return organisationCredits?.isRestricted ?? false;
  }, [userContext?.permissions?.accessScope, organisationCredits?.isRestricted]);

  const [showDirectSelect, setShowDirectSelect] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<ModelCategory>('Edit');
  const [selectedSubCategory, setSelectedSubCategory] = useState<ModelSubCategory | undefined>(undefined);
  const modelVisibility = useKernelStore(state => state.userPreferences?.modelVisibility);
  const studioModelUsage = useKernelStore(state => state.userContext?.studioModelUsage);
  const projectType = useKernelStore(state => projectId
    ? state.inventory.Project.registry[projectId]?.projectType ?? null
    : null);
  const projectCompatibleModelIds = useMemo(
    () => new Set(filterStudioModelIdsForProjectType(ALL_MODELS_IN_UI, projectType)),
    [projectType],
  );
  // Families the account cannot spend on at all (billing carve-out) or this server cannot run, a
  // different system from the plan's cheap-only scope: this one hides the model whatever the scope says.
  const withheldFamilies = useMemo(
    () => clientAccessSuspension(
      userContext?.accessSuspension,
      userContext?.organisationId,
      userContext?.teamId,
    ).withheldModelFamilies,
    [userContext?.accessSuspension, userContext?.organisationId, userContext?.teamId],
  );
  const directModelOptions = useMemo(
    () => ALL_MODELS_IN_UI.filter(id => projectCompatibleModelIds.has(id) && !isWithheldAsUnrunnable(id, withheldFamilies)),
    [projectCompatibleModelIds, withheldFamilies],
  );
  const modelAccessStateById = useMemo(() => {
    const states = buildStudioFrontendModelAccessState({
      organisationId: userContext?.organisationId,
      accessScope: userContext?.permissions?.accessScope,
      organisationRestricted: organisationCredits?.isRestricted,
      withheldModelFamilies: withheldFamilies,
      modelVisibility,
    });
    return new Map(states.map((state) => [state.modelId, state]));
  }, [userContext?.organisationId, userContext?.permissions?.accessScope, organisationCredits?.isRestricted, withheldFamilies, modelVisibility]);

  const visibleModelHierarchy = useMemo(() => {
    const filteredHierarchy: CategoryUIData[] = [];
    const compareModels = compareStudioModels(studioModelUsage);

    for (const categoryData of modelHierarchyData) {
        const visibleModels = categoryData.models.filter(model => {
          const accessState = modelAccessStateById.get(model.id);
          return projectCompatibleModelIds.has(model.id) && (accessState?.isInActiveList ?? false);
        });

        if (visibleModels.length > 0) {
            filteredHierarchy.push({
                ...categoryData,
                models: visibleModels.sort((a, b) => compareModels(a.id, b.id)),
            });
        }
    }
    return filteredHierarchy;
  }, [modelAccessStateById, projectCompatibleModelIds, studioModelUsage]);

  const findModelIdByDetails = useCallback((
    modelName: string,
    category: ModelCategory,
    subCategory?: ModelSubCategory
  ): string | undefined => {
      const categoryData = visibleModelHierarchy.find(c => c.category === category);
      if (!categoryData) return undefined;
      const model = categoryData.models.find(m => {
          const nameMatch = m.name === modelName;
          const subCatMatch = (!subCategory || m.subCategory === subCategory);
          return nameMatch && subCatMatch;
      });
      return model?.id;
  }, [visibleModelHierarchy]);

  const subCategoryOptions = useMemo(() => {
    if (selectedCategory === 'Generate') return generateModelSubCategories;
    if (selectedCategory === 'Edit') return editModelSubCategories;
    return [];
  }, [selectedCategory]);

  const getDefaultSubCategory = useCallback((category: ModelCategory): ModelSubCategory | undefined => {
    if (category === 'Generate') return 'Basic';
    if (category === 'Edit') return 'Full Area';
    return undefined;
  }, []);


  useEffect(() => {
    const modelData = findModelDataById(currentModelId);

    if (isRestricted && !CHEAP_STUDIO_MODEL_IDS.includes(currentModelId)) {
        // Prioritize nano-banana as the default fallback in restricted mode
        let fallbackModelId: string | undefined;
        
        // 1. Try to find nano-banana explicitly
        const nanoBanana = visibleModelHierarchy
            .flatMap(c => c.models)
            .find(m => m.id === 'nano-banana');
            
        if (nanoBanana) {
            fallbackModelId = nanoBanana.id;
        } else {
            // 2. Otherwise take the very first available model
            fallbackModelId = visibleModelHierarchy[0]?.models[0]?.id;
        }

        if (fallbackModelId) {
            onModelChange(fallbackModelId);
            return;
        }
    }

    if (modelData) {
        const needsCategoryUpdate = modelData.category !== selectedCategory;
        const needsSubCategoryUpdate = modelData.subCategory !== selectedSubCategory;

        if (needsCategoryUpdate) {
            Promise.resolve().then(() => {
              setSelectedCategory(modelData.category);
              setSelectedSubCategory(modelData.subCategory ?? getDefaultSubCategory(modelData.category));
            });
        } else if (needsSubCategoryUpdate) {
            Promise.resolve().then(() => {
              setSelectedSubCategory(modelData.subCategory ?? getDefaultSubCategory(modelData.category));
            });
        }
    } else if (!showDirectSelect) {
        const defaultModelData = findModelDataById(visibleModelHierarchy[0]?.models[0]?.id || '');
        if (defaultModelData) {
            Promise.resolve().then(() => {
              setSelectedCategory(defaultModelData.category);
              setSelectedSubCategory(defaultModelData.subCategory ?? getDefaultSubCategory(defaultModelData.category));
            });
        } else {
            Promise.resolve().then(() => {
              setSelectedCategory('Edit');
              setSelectedSubCategory('Full Area');
            });
        }
    }
}, [currentModelId, showDirectSelect, selectedCategory, selectedSubCategory, visibleModelHierarchy, getDefaultSubCategory, isRestricted, onModelChange]);


  const tabsConfig = useMemo(() => {
    return visibleModelHierarchy.map(data => ({ key: data.category, icon: data.icon }));
  }, [visibleModelHierarchy]);

  const shouldShowCategoryPicker = visibleModelHierarchy.length > 1;

  const supportsSubCategories = useMemo(() => {
    return selectedCategory === 'Generate' || selectedCategory === 'Edit';
  }, [selectedCategory]);

  const availableSubCategoryOptions = useMemo(() => {
    if (!supportsSubCategories) return [];
    const categoryData = visibleModelHierarchy.find(data => data.category === selectedCategory);
    if (!categoryData) return [];
    const available = new Set(categoryData.models.map(model => model.subCategory).filter(Boolean) as ModelSubCategory[]);
    return subCategoryOptions.filter(option => available.has(option));
  }, [supportsSubCategories, visibleModelHierarchy, selectedCategory, subCategoryOptions]);

  const activeSubCategoryForSelection = useMemo(() => {
    if (!supportsSubCategories) return undefined;
    if (selectedSubCategory && availableSubCategoryOptions.includes(selectedSubCategory)) {
      return selectedSubCategory;
    }
    return availableSubCategoryOptions[0] ?? getDefaultSubCategory(selectedCategory);
  }, [supportsSubCategories, selectedSubCategory, availableSubCategoryOptions, getDefaultSubCategory, selectedCategory]);

  const shouldShowSubCategoryPicker = supportsSubCategories && availableSubCategoryOptions.length > 1;

  const modelsForFinalSelect = useMemo(() => {
    const categoryData = visibleModelHierarchy.find(data => data.category === selectedCategory);
    if (!categoryData) return [];
    let filteredModels = categoryData.models;
    if (supportsSubCategories) {
        const activeSubCategory = activeSubCategoryForSelection;
        filteredModels = filteredModels.filter(model => model.subCategory === activeSubCategory);
    }
    return filteredModels;
  }, [selectedCategory, supportsSubCategories, visibleModelHierarchy, activeSubCategoryForSelection]);

  const modelNamesForFinalSelect: string[] = useMemo(() => {
      return [...new Set(modelsForFinalSelect.map(model => studioModelFamilyOf(model.id)?.name ?? model.name))];
  }, [modelsForFinalSelect]);

  const currentFamily = studioModelFamilyOf(currentModelId);
  const familyVersionOptions = useMemo(
    () => currentFamily ? visibleFamilyVersions(currentFamily, modelsForFinalSelect.map(model => model.id)).map(version => ({ value: version.id, label: version.label })) : [],
    [currentFamily, modelsForFinalSelect],
  );
  const shouldShowFamilyVersionPicker = familyVersionOptions.some(option => option.value === currentModelId);

  const currentModelName = useMemo(() => {
    const knownModel = findModelDataById(currentModelId);
    return knownModel ? knownModel.name : currentModelId;
  }, [currentModelId]);

  const handleCategoryChange = useCallback((newCategoryKey: string) => {
    const newCategory = newCategoryKey as ModelCategory;
    setSelectedCategory(newCategory);
    const categorySupportsSubCats = newCategory === 'Generate' || newCategory === 'Edit';
    let firstModel: ModelInfo | undefined;
    if (categorySupportsSubCats) {
        const defaultSubCategory = getDefaultSubCategory(newCategory);
        setSelectedSubCategory(defaultSubCategory);
        firstModel = visibleModelHierarchy.find(d => d.category === newCategory)?.models.find(m => !defaultSubCategory || m.subCategory === defaultSubCategory);
    } else {
        setSelectedSubCategory(undefined);
        firstModel = visibleModelHierarchy.find(d => d.category === newCategory)?.models[0];
    }
    if (firstModel && firstModel.id !== currentModelId) {
      kernel.current?.logUserAction('Model Category Changed', {
        old_category: selectedCategory, // Track the category *before* the change
        new_category: newCategory,
        selected_model_id: firstModel.id, // The ID of the model automatically selected
        selected_model_name: firstModel.name
      });
      onModelChange(firstModel.id);
    } else if (!firstModel) {
      console.warn(`No default model found for category ${newCategory}`);
      kernel.current?.logUserAction('Model Category Change Failed', {
       old_category: selectedCategory,
       new_category: newCategory,
       reason: 'No default model found'
      });
    }
  }, [currentModelId, getDefaultSubCategory, kernel, onModelChange, selectedCategory, visibleModelHierarchy]);

  const handleSubCategoryChange = useCallback((newSubCategoryValue: string) => {
    const newSubCategory = newSubCategoryValue as ModelSubCategory;
    setSelectedSubCategory(newSubCategory);
    let firstModel: ModelInfo | undefined;
    firstModel = visibleModelHierarchy.find(d => d.category === selectedCategory)?.models.find(m => m.subCategory === newSubCategory);
    if (firstModel && firstModel.id !== currentModelId) {
      kernel.current?.logUserAction('Model SubCategory Changed', {
        category: selectedCategory,
        old_subcategory: selectedSubCategory,
        new_subcategory: newSubCategory,
        selected_model_id: firstModel.id,
        selected_model_name: firstModel.name,
      });
      onModelChange(firstModel.id);
    } else if (!firstModel) {
      console.warn(`No model found for ${selectedCategory}/${newSubCategory}`);
      kernel.current?.logUserAction('Model SubCategory Change Failed', {
       category: selectedCategory,
       old_subcategory: selectedSubCategory,
       new_subcategory: newSubCategory,
       reason: 'No model found'
      });
    }
  }, [currentModelId, kernel, onModelChange, selectedCategory, selectedSubCategory, visibleModelHierarchy]);

  const handleModelSelectionChange = useCallback((selectedModelName: string) => {
      let selectedModelId: string | undefined;
      const selectedFamily = studioModelFamilyNamed(selectedModelName);

      if (showDirectSelect) {
          selectedModelId = findModelIdByName(selectedModelName);
      } else if (selectedFamily) {
          selectedModelId = resolveFamilyVersion(selectedFamily, currentModelId, modelsForFinalSelect.map(model => model.id));
      } else {
          selectedModelId = findModelIdByDetails(
              selectedModelName,
              selectedCategory,
              supportsSubCategories ? activeSubCategoryForSelection : undefined
          );
      }

      const oldModelData = findModelDataById(currentModelId);

      if (selectedModelId && selectedModelId !== currentModelId) {
          kernel.current?.logUserAction('Model Selected', {
            old_model_id: currentModelId,
            old_model_name: oldModelData?.name,
            new_model_id: selectedModelId,
            new_model_name: findModelDataById(selectedModelId)?.name,
            category: selectedCategory, // Current category/subcategory/controltype context
            subcategory: activeSubCategoryForSelection,
            source: showDirectSelect ? 'Direct Select' : 'Categorized Select'
          });
          onModelChange(selectedModelId);
      } else if (!selectedModelId) {
          console.error(`Could not find model ID for name "${selectedModelName}" with current filters (Category: ${selectedCategory}, SubCat: ${activeSubCategoryForSelection}).`);
           kernel.current?.logUserAction('Model Selection Failed', {
            attempted_name: selectedModelName,
            category: selectedCategory,
            subcategory: activeSubCategoryForSelection,
            source: showDirectSelect ? 'Direct Select' : 'Categorized Select',
            reason: 'Model ID not found'
          });
      }
  }, [activeSubCategoryForSelection, currentModelId, findModelIdByDetails, kernel, modelsForFinalSelect, onModelChange, selectedCategory, showDirectSelect, supportsSubCategories]);

  const handleFamilyVersionChange = useCallback((versionId: string) => {
      kernel.current?.logUserAction('Model Selected', {
        old_model_id: currentModelId,
        old_model_name: findModelDataById(currentModelId)?.name,
        new_model_id: versionId,
        new_model_name: findModelDataById(versionId)?.name,
        category: selectedCategory,
        subcategory: activeSubCategoryForSelection,
        source: 'Version Select'
      });
      onModelChange(versionId);
  }, [activeSubCategoryForSelection, currentModelId, kernel, onModelChange, selectedCategory]);

  const handleRestoreDefaults = useCallback(() => {
    kernel.current?.logUserAction('Restore Model Defaults Clicked', { model_id: currentModelId, model_name: currentModelName }); // added analytics
    onRestoreDefaults();
  }, [currentModelId, currentModelName, kernel, onRestoreDefaults]);

  const handleOpenModelGallery = useCallback(() => {
    const selectableModelIds = modelsForFinalSelect.map((model) => model.id);
    kernel.current?.logUserAction('Model Gallery Modal Opened', {
      source_component: 'ModelSelector',
      model_id: currentModelId,
      model_name: currentModelName,
      selectable_model_count: selectableModelIds.length,
    });
    kernel.current?.openModal('ModelGallery', {
      modelIds: selectableModelIds,
      selectedModelId: currentModelId,
      category: selectedCategory,
      subCategory: activeSubCategoryForSelection ?? null,
    });
  }, [activeSubCategoryForSelection, currentModelId, currentModelName, kernel, modelsForFinalSelect, selectedCategory]);


  return (
    <div className="flex flex-col">

      {showDirectSelect ? (
        <Select
          id={'direct-model-select'} // Added ID
          value={currentModelId}
          options={directModelOptions}
          onChange={handleModelSelectionChange} // Uses tracked handler
        />
      ) : (
        <div className="flex flex-col gap-2">
          {shouldShowCategoryPicker && (
            <Tabs tabs={tabsConfig} activeTab={selectedCategory} onTabChange={handleCategoryChange} fullWidth={true} showBorderBottom={false} />
          )}

          <div className="flex fullWidth flex-row items-center space-x-4">
            {shouldShowSubCategoryPicker && (
               <Select
                 id={`model-sub-category-select`}
                 label="Type"
                 value={activeSubCategoryForSelection || ''}
                 options={availableSubCategoryOptions}
                 onChange={handleSubCategoryChange} // Uses tracked handler
                 style={NARROW_SELECT_STYLE}
               />
            )}
          </div>

          {modelNamesForFinalSelect.length > 0 ? (
            <div className="flex fullWidth flex-row items-center justify-between space-x-2">
              <Select
                id={`model-select-${selectedCategory}-${selectedSubCategory}`} // Added ID
                label="Model"
                value={currentFamily?.name ?? currentModelName}
                options={modelNamesForFinalSelect}
                onChange={handleModelSelectionChange} // Uses tracked handler
                style={NARROW_SELECT_STYLE}
              />
              <div className="flex items-center gap-1">
                <ActionButton
                  onClick={handleOpenModelGallery}
                  tooltipText="Open model gallery"
                  icon={Info}
                />
                <ActionButton onClick={handleRestoreDefaults} tooltipText="Restore default params for selected model" icon={RotateCcw} /> {/* Uses tracked handler */}
              </div>
            </div>
          ) : (
            <p className="text-xs text-center text-gray-500 py-2">
              No models available for '{selectedCategory}'{activeSubCategoryForSelection ? ` / '${activeSubCategoryForSelection}'` : ''}.
            </p>
          )}

          {shouldShowFamilyVersionPicker && (
            <Select
              id="model-family-version-select"
              label="Version"
              value={currentModelId}
              options={familyVersionOptions}
              onChange={handleFamilyVersionChange}
              style={NARROW_SELECT_STYLE}
            />
          )}
        </div>
      )}
    </div>
  );
});

ModelSelector.displayName = "ModelSelector";
export default ModelSelector;
